import { getHopinApiRoot } from '@hopin-team/config-hopin-constants';

interface EndpointInterface {
  STRIPE_KEY: string;
  AMPLITUDE_KEY: string;
  EVENT_SETTINGS: (eventSlug: string) => string;
  EVENT_DETAILS: (eventSlug: string) => string;
  SERIES_EVENT_DETAILS: (
    eventSlug: string,
    seriesExternalId: string | null,
  ) => string;
  WIDGET_TICKETS: (eventExternalIds: string[]) => string;
  WIDGET_QUESTIONS: (ticketExternalId: string) => string;
  EVENT_DETAILS_WIDGET: (eventSlug: string, widgetId: string) => string;
  EVENT_DETAILS_ONSITE_WIDGET: (eventSlug: string) => string;
  WAITLIST: (ticketId: string) => string;
  ORDER_SUMMARY: (eventSlug: string) => string;
  ORDER_SUMMARY_SERIES: (eventSlug: string, seriesId: string) => string;
  VALIDATE_PROMO_CODE: (eventSlug: string, promoCode: string) => string;
  PAYMENT_TOKEN: (eventSlug: string) => string;
  SERIES_PAYMENT_TOKEN: (eventSlug: string) => string;
  COMPLETE_SERIES_ORDER: (eventSlug: string) => string;
  FINALIZE_ORDER: (eventSlug: string) => string;
  VALIDATE_TICKETS: (eventSlug: string) => string;
  VALIDATE_SERIES_PROMO_CODE: (
    promoCode: string,
    ticketExternalIds: string[],
  ) => string;
  REDEEM_CODE: (eventSlug: string, ticketId: string) => string;
  MAGIC_LINK: (eventSlug: string) => string;
  ORDER_INVITES: (eventSlug: string, orderSlug: string) => string;
  SERIES_ORDER_INVITES: (seriesExternalId: string) => string;
  FORM_ANSWERS: (eventSlug: string, inviteId: number) => string;
  FORM_ANSWERS_DIRECT: (eventSlug: string) => string;
  SERIES_FORM_ANSWERS: (seriesExternalId: string) => string;
  REGISTRATION_STATUS: (eventSlug: string) => string;
  VALIDATE_INVITE: (eventSlug: string) => string;
  RESEND_INVITE: (eventSlug: string) => string;
  SESSIONS_FOR_TICKET: (eventSlug: string, ticketExternalId: string) => string;
  ADD_SESSION_TO_ORDER: (eventSlug: string, orderSlug: string) => string;
  REMOVE_SESSION_FROM_ORDER: (eventSlug: string, orderSlug: string) => string;
  START_TIMER: (eventSlug: string, orderSlug: string) => string;
}

const HOST = {
  DEVELOP: 'dev.registration.hopin.com',
  STAGING: 'registration-stg.hopin.com',
  PRODUCTION: 'registration.hopin.com',
  RNG_PRODUCTION: 'registration.events.ringcentral.com',
};

const STRIPE_PUBLISHABLE_KEY_TEST = 'pk_test_POdVaUa0V4ZNprNEjpp4cdGg';
const STRIPE_PUBLISHABLE_KEY_LIVE = 'pk_live_48JAIqmrk3fcmlbsJaiJ5ked';

const AMPLITUDE_KEY_DEV = 'dae8868d3b565e99e7d29a2a59921378';
const AMPLITUDE_KEY_PROD = '654c67be73a8586657c8d43fc76c0488';

const endpoints: () => <K extends keyof EndpointInterface>(
  key: K,
) => EndpointInterface[K] = () => {
  const { hostname } = window.location;
  const PUBLIC_API_ROOT = 'public/registrations_component';

  const config: EndpointInterface = {
    STRIPE_KEY:
      hostname === HOST.PRODUCTION || hostname === HOST.RNG_PRODUCTION
        ? STRIPE_PUBLISHABLE_KEY_LIVE
        : STRIPE_PUBLISHABLE_KEY_TEST,
    AMPLITUDE_KEY:
      hostname === HOST.PRODUCTION || hostname === HOST.RNG_PRODUCTION
        ? AMPLITUDE_KEY_PROD
        : AMPLITUDE_KEY_DEV,
    VALIDATE_TICKETS: (eventSlug: string) =>
      `api/registrations_component/${eventSlug}/tickets/validate`,
    EVENT_SETTINGS: (eventSlug: string): string =>
      `${PUBLIC_API_ROOT}/public_event_settings/${eventSlug}`,
    EVENT_DETAILS: (eventSlug: string): string =>
      `${PUBLIC_API_ROOT}/${eventSlug}/default_widget`,
    SERIES_EVENT_DETAILS: (
      eventSlug: string,
      seriesExternalId: string | null,
    ): string => {
      const query = seriesExternalId
        ? `?series_external_id=${seriesExternalId}`
        : '';
      return `${PUBLIC_API_ROOT}/${eventSlug}/series_default_widget${query}`;
    },
    WIDGET_TICKETS: eventExternalIds => {
      const queryParams = new URLSearchParams();
      eventExternalIds.forEach(id => {
        queryParams.append('event_external_id[]', id);
      });

      return `${PUBLIC_API_ROOT}/widget_tickets?${queryParams}`;
    },
    WIDGET_QUESTIONS: ticketExternalId => {
      return `${PUBLIC_API_ROOT}/widget_questions/${ticketExternalId}`;
    },
    EVENT_DETAILS_WIDGET: (eventSlug: string, widgetId: string): string =>
      `${PUBLIC_API_ROOT}/${eventSlug}/embeddable/${widgetId}`,
    EVENT_DETAILS_ONSITE_WIDGET: (eventSlug: string): string =>
      `${PUBLIC_API_ROOT}/${eventSlug}/onsite`,
    ORDER_SUMMARY: (eventSlug: string): string =>
      `${PUBLIC_API_ROOT}/orders/${eventSlug}/calculate`,
    ORDER_SUMMARY_SERIES: (eventSlug: string): string =>
      `${PUBLIC_API_ROOT}/orders/${eventSlug}/series_calculate`,
    WAITLIST: (ticketID: string) =>
      `${PUBLIC_API_ROOT}/ticket_waitlists/${ticketID}`,
    VALIDATE_PROMO_CODE: (eventSlug: string, promoCode: string): string =>
      `${PUBLIC_API_ROOT}/promo_codes/${eventSlug}/${promoCode}/valid`,
    VALIDATE_SERIES_PROMO_CODE: (
      promoCode: string,
      ticketExternalIds: string[],
    ): string => {
      const queryParams = new URLSearchParams();
      ticketExternalIds.forEach(ticketId => {
        queryParams.append('ticket_external_id[]', ticketId);
      });
      return `${PUBLIC_API_ROOT}/promo_codes/${promoCode}/valid_for/?${queryParams}`;
    },
    PAYMENT_TOKEN: (eventSlug: string): string =>
      `${PUBLIC_API_ROOT}/orders/${eventSlug}/payment`,
    SERIES_PAYMENT_TOKEN: (eventSlug: string): string =>
      `${PUBLIC_API_ROOT}/orders/${eventSlug}/series_payment`,
    COMPLETE_SERIES_ORDER: (eventSlug: string): string =>
      `${PUBLIC_API_ROOT}/orders/${eventSlug}/series_complete`,
    FINALIZE_ORDER: (eventSlug: string): string =>
      `${PUBLIC_API_ROOT}/orders/${eventSlug}/complete`,
    REDEEM_CODE: (eventSlug: string, ticketId: string): string =>
      `${PUBLIC_API_ROOT}/redeem_codes/${eventSlug}/${ticketId}`,
    MAGIC_LINK: (eventSlug: string): string =>
      `${PUBLIC_API_ROOT}/magic_links/${eventSlug}`,
    ORDER_INVITES: (eventSlug: string, orderSlug: string): string =>
      `${PUBLIC_API_ROOT}/invites/${eventSlug}/${orderSlug}`,
    SERIES_ORDER_INVITES: (seriesExternalId: string): string =>
      `${PUBLIC_API_ROOT}/series_invites/${seriesExternalId}`,
    FORM_ANSWERS: (eventSlug: string, inviteId: number): string =>
      `${PUBLIC_API_ROOT}/custom_form_answers/${eventSlug}/invite/${inviteId}`,
    FORM_ANSWERS_DIRECT: (eventSlug: string): string =>
      `registrations_component/custom_form_answers/${eventSlug}/direct`,
    SERIES_FORM_ANSWERS: (seriesExternalId: string): string =>
      `${PUBLIC_API_ROOT}/series_answers/${seriesExternalId}`,
    REGISTRATION_STATUS: (eventSlug: string) =>
      `registrations_component/${eventSlug}/tickets/registration_status`,
    VALIDATE_INVITE: (eventSlug: string) =>
      `${PUBLIC_API_ROOT}/invites/${eventSlug}/validate`,
    RESEND_INVITE: (eventSlug: string) =>
      `${PUBLIC_API_ROOT}/invites/${eventSlug}/resend_email`,
    SESSIONS_FOR_TICKET: (eventSlug: string, ticketExternalId: string) =>
      `${getHopinApiRoot(
        'v2',
      )}/schedules/public/${eventSlug}/items/?ticket_external_id=${ticketExternalId}`,
    ADD_SESSION_TO_ORDER: (eventSlug: string, orderSlug: string) =>
      `/public/registrations_component/orders/${eventSlug}/${orderSlug}/add-items`,
    REMOVE_SESSION_FROM_ORDER: (eventSlug: string, orderSlug: string) =>
      `/public/registrations_component/orders/${eventSlug}/${orderSlug}/remove-items`,
    START_TIMER: (eventSlug: string, orderSlug: string) =>
      `/public/registrations_component/orders/${eventSlug}/${orderSlug}/start_timer`,
  };

  const get = <K extends keyof EndpointInterface>(
    key: K,
  ): EndpointInterface[K] => {
    const val = config[key];
    if (val === undefined) throw new Error(`Missing configuration key: ${key}`);
    return val;
  };

  return get;
};

export { endpoints };
